import { type ReactNode, Suspense, useEffect } from 'react';
import { useLocation } from 'wouter';

import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { UnifiedSidebar } from '@/components/sidebar';
import { Phase1Column } from '@/components/sidebar/Phase1Column';
import { GlobalHeader } from '@/components/GlobalHeader';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { MobileTabBar } from '@/components/MobileTabBar';
import { UserMenu } from '@/components/sidebar/UserMenu';
import { phase1ModuleFor } from '@/lib/phase1Paths';

interface Phase1LayoutProps {
  children: ReactNode;
}

/**
 * AR-445 — the shell for every Phase-1 screen.
 *
 * The Settings module's structure: GlobalHeader → [ Level 1 rail | Level 2
 * column | content ]. The rail is UnifiedSidebar, which renders the Phase-1
 * rail while the flags are on; the column is the current module's, and a
 * module without one (CRM (legacy), Calendar, News…) simply has none.
 *
 * On a phone the rails leave the flow and the tab bar takes over; the module's
 * column moves into the More sheet above the full module list.
 */
export default function Phase1Layout({ children }: Phase1LayoutProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { favicon } = useTheme();
  const [location, navigate] = useLocation();
  const module = phase1ModuleFor(location);

  useEffect(() => {
    if (!favicon) return;
    const el = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
    if (el) el.href = favicon;
  }, [favicon]);

  const shouldRedirect = !isAuthenticated && !isLoading;
  useEffect(() => {
    if (shouldRedirect) navigate('/login');
  }, [shouldRedirect, navigate]);

  // A new page starts at its top, the way a page load would.
  useEffect(() => {
    document.querySelector('[data-phase1-main]')?.scrollTo({ top: 0 });
  }, [location]);

  if (!isAuthenticated) {
    if (isLoading) return <div>Loading...</div>;
    return null;
  }

  return (
    <div
      data-phase1-shell
      data-location={location}
      className="flex h-screen flex-col overflow-hidden bg-gray-50 dark:bg-[#101415]"
    >
      <GlobalHeader />
      <ImpersonationBanner />
      <div className="flex min-h-0 flex-1" data-phase1-body>
        <div className="hidden md:flex">
          <UnifiedSidebar />
        </div>
        {module?.column?.length ? (
          <div className="hidden md:flex">
            <Phase1Column key={module.id} module={module} />
          </div>
        ) : null}
        <main
          id="main-content"
          tabIndex={-1}
          className="min-w-0 flex-1 overflow-auto bg-white outline-none dark:bg-[#101415]"
          data-phase1-main
        >
          <Suspense
            fallback={
              <div role="status" className="p-8 text-[15px] text-gray-500">
                Loading…
              </div>
            }
          >
            {children}
          </Suspense>
        </main>
      </div>
      <MobileTabBar footer={<UserMenu expanded inline />}>
        {module?.column?.length
          ? (close) => <Phase1Column module={module} inDrawer onNavigated={close} />
          : undefined}
      </MobileTabBar>
    </div>
  );
}
